// src/components/chat/PathLibrary.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const PathLibrary = ({ isOpen, onClose, onSelectPreset }) => {
  const [presets, setPresets] = useState([]);
  const [filteredPresets, setFilteredPresets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  
  useEffect(() => {
    // This would typically come from an API or a file
    const presetsData = [
      {
        "SalesStage": "Early Funnel – Pre‑Outreach",
        "Role": "AE",
        "Question": "What's a high‑impact opening hypothesis for this account based on its recent filings and market trends?"
      },
      {
        "SalesStage": "Early Funnel – Post‑Outreach / Re‑engagement",
        "Role": "AE",
        "Question": "What personalized insight tile can I send to reignite interest with this prospect?"
      },
      {
        "SalesStage": "Early Funnel – Pre‑Meeting / Discovery",
        "Role": "AE",
        "Question": "Which top business drivers and pain points should I highlight in my discovery deck?"
      },
      {
        "SalesStage": "Middle Funnel – Validation / Stakeholder Expansion",
        "Role": "AE",
        "Question": "What concise story can I equip my internal champion to align our solution with exec priorities?"
      },
      {
        "SalesStage": "Middle Funnel – Validation / Stakeholder Expansion",
        "Role": "AE",
        "Question": "How should I tailor a narrative for the new stakeholder [Role] to illustrate our value?"
      },
      {
        "SalesStage": "Early Funnel – Pre‑Discovery Prep",
        "Role": "SE",
        "Question": "What technical hypothesis can I form about their current state and desired future?"
      },
      {
        "SalesStage": "Early Funnel – Pre‑Discovery Prep",
        "Role": "SE",
        "Question": "Which industry trends and competitor moves should I weave into my discovery narrative?"
      },
      {
        "SalesStage": "Early Funnel – Pre‑Discovery Prep",
        "Role": "SE",
        "Question": "How can I visually frame their key technical challenges and our solution's before‑and‑after?"
      },
      {
        "SalesStage": "Middle Funnel",
        "Role": "SE",
        "Question": "What role‑based value summary should I present to each stakeholder in the room?"
      },
      {
        "SalesStage": "Early Funnel – Pre‑QBR / Re‑engagement",
        "Role": "CSM",
        "Question": "What forward‑looking hypothesis should guide my check‑in with this customer?"
      },
      {
        "SalesStage": "Post‑Sale – Nurture / Expansion",
        "Role": "CSM",
        "Question": "Which tailored insight can I share now to uncover expansion opportunities?"
      },
      {
        "SalesStage": "Post‑Sale – Onboarding / QBR",
        "Role": "CSM",
        "Question": "What key outcomes and metrics should I include in the next executive business review?"
      },
      {
        "SalesStage": "Post‑Sale – Onboarding / Expansion",
        "Role": "CSM",
        "Question": "How can I frame an expansion story that aligns our platform to their new priorities?"
      },
      {
        "SalesStage": "Post‑Sale – Onboarding Readiness",
        "Role": "CSM",
        "Question": "What business context and champion quotes should go into the onboarding readiness deck?"
      }
    ];
    
    setPresets(presetsData);
    setFilteredPresets(presetsData);
  }, []);
  
  // Filter presets based on search term, selected stage, and selected role
  useEffect(() => {
    let filtered = [...presets];
    
    if (searchTerm) {
      filtered = filtered.filter(preset => 
        preset.Question.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedStage) {
      filtered = filtered.filter(preset => 
        preset.SalesStage === selectedStage
      );
    }
    
    if (selectedRole) {
      filtered = filtered.filter(preset => 
        preset.Role === selectedRole
      );
    }
    
    setFilteredPresets(filtered);
  }, [searchTerm, selectedStage, selectedRole, presets]);
  
  // Get unique sales stages and roles for filters
  const salesStages = [...new Set(presets.map(preset => preset.SalesStage))];
  const roles = [...new Set(presets.map(preset => preset.Role))];
  
  // Handle card selection
  const handleSelectPreset = (question) => {
    onSelectPreset(question);
    onClose();
  };
  
  // Reset filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedStage('');
    setSelectedRole('');
  };
  
  // Calculate grid layouts based on sales stages
  const presetsByStage = {};
  filteredPresets.forEach(preset => {
    if (!presetsByStage[preset.SalesStage]) {
      presetsByStage[preset.SalesStage] = [];
    }
    presetsByStage[preset.SalesStage].push(preset);
  });
  
  // Animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };
  
  const modalVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: 'spring', damping: 25, stiffness: 500 } }
  };
  
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: custom => ({
      opacity: 1,
      y: 0,
      transition: { delay: custom * 0.05, duration: 0.3 }
    }),
    hover: { 
      scale: 1.01, 
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)", 
      transition: { duration: 0.2 } 
    }
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={overlayVariants}
          onClick={onClose}
        >
          <motion.div 
            className="bg-zinc-900  rounded-xl w-3/4 h-3/4 overflow-hidden flex flex-col"
            variants={modalVariants}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-black/20 p-6  flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <h2 className="text-3xl sp-big-heading text-zinc-50">Conversation Paths</h2>
              
              {/* Filters */}
              <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-4 w-full md:w-auto">
                {/* Search input */}
                <div className="relative w-full md:w-64">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search prompts..."
                    className="w-full bg-black/30 border border-zinc-700 text-zinc-200 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Stage select */}
                <select
                  value={selectedStage}
                  onChange={e => setSelectedStage(e.target.value)}
                  className="w-full md:w-auto bg-black/30 border border-zinc-700 text-zinc-200 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  <option value="">All Stages</option>
                  {salesStages.map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
                
                  <div className=" ">
                    for
                  </div>

                {/* Role select */}
                <select
                  value={selectedRole}
                  onChange={e => setSelectedRole(e.target.value)}
                  className="w-full md:w-auto bg-black/30 border border-zinc-700 text-zinc-200 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  <option value="">All Roles</option>
                  {roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
                
                {/* Reset button */}
                {(searchTerm || selectedStage || selectedRole) && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleResetFilters}
                    className="w-full md:w-auto bg-black/30 hover:bg-sky-500 text-white py-2 px-4 rounded-lg"
                  >
                    Reset
                  </motion.button>
                )}

<div className=" ">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="bg-black/30 hover:bg-sky-500 text-white p-2 rounded-full flex items-center justify-center w-8 h-8"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </motion.button>
            </div>

              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {Object.keys(presetsByStage).length > 0 ? (
                Object.entries(presetsByStage).map(([stage, stagePresets]) => (
                  <div key={stage} className="mb-8">
                    <h3 className="text-lg ml-4 text-zinc-200 mb-4">{stage}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {stagePresets.map((preset, index) => (
                        <motion.div
                          key={`${preset.Role}-${index}`}
                          custom={index}
                          initial="hidden"
                          animate="visible"
                          whileHover="hover"
                          variants={cardVariants}
                          onClick={() => handleSelectPreset(preset.Question)}
                          className="cursor-pointer bg-black/20 border border-transparent hover:border-sky-500 rounded-lg p-4 flex flex-col"
                        >
                          <div className="text-xs font-semibold text-sky-300 mb-1">{preset.Role}</div>
                          <p className="text-zinc-200">{preset.Question}</p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-64">
                  <div className="text-zinc-400 mb-2">No matching presets found</div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleResetFilters}
                    className="bg-black/30 hover:bg-sky-500 text-white py-2 px-4 rounded-lg mt-4"
                  >
                    Reset Filters
                  </motion.button>
                </div>
              )}
            </div>
            
            {/* Footer */}
            {/* Close button in upper right */}
            
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PathLibrary;